import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import type { GetSlotsResponse, Slot } from '../types';

type ServiceUserEnvelope<T> = {
  message?: string;
  status?: string;
  responseBody?: T;
  errorCode?: number;
  errorMessage?: string | null;
};

const EMPTY_SLOTS: GetSlotsResponse = {
  trainer_id: '',
  date: '',
  student_timezone: '',
  slots: { '15_min': [], '30_min': [] },
};

// `date` must be sent as YYYY-MM-DD (router-level validation); the
// internal slot-listing service then converts to dd/MM/yyyy for the
// TMS layer. Anchoring the wire format on the parent app keeps the
// frontend independent of the legacy TMS format.
export const getParentConnectSlots = async (
  variantId: string,
  dateYmd: string
): Promise<GetSlotsResponse> => {
  const data: ServiceUserEnvelope<GetSlotsResponse> = await axios.get(
    '/user/v1/trainer-slots/parent-connect',
    { params: { variant_id: variantId, date: dateYmd } }
  );
  return data?.responseBody ?? EMPTY_SLOTS;
};

// Build deterministic mock slots over the given local date so the
// dev experience still feels real (Day vs Evening buckets populated,
// epochs ordered, slot duration consistent with the request).
const buildMockSlots = (dateYmd: string): GetSlotsResponse['slots'] => {
  // Fall back to "today" if the URL date param is malformed - keeps
  // the dev page rendering instead of erroring out.
  const [yStr, mStr, dStr] = dateYmd.split('-');
  const year = Number.parseInt(yStr ?? '2026', 10);
  const month = Number.parseInt(mStr ?? '1', 10);
  const day = Number.parseInt(dStr ?? '1', 10);
  const base = new Date(year, month - 1, day, 0, 0, 0, 0);

  const slotsFor = (durationMin: number, hours: number[]): Slot[] =>
    hours.map(hour => {
      const start = new Date(base);
      start.setHours(hour, 0, 0, 0);
      const startEpoch = Math.floor(start.getTime() / 1000);
      return {
        start_time: `${hour.toString().padStart(2, '0')}:00`,
        end_time: `${hour.toString().padStart(2, '0')}:${durationMin
          .toString()
          .padStart(2, '0')}`,
        start_epoch: startEpoch,
        end_epoch: startEpoch + durationMin * 60,
      };
    });

  return {
    '15_min': slotsFor(15, [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21]),
    '30_min': slotsFor(30, [9, 10, 11, 14, 15, 17, 18, 19, 20]),
  };
};

export const getParentConnectSlotsMockHandler = http.get(
  `${env.API_URL}/user/v1/trainer-slots/parent-connect`,
  async ({ request }) => {
    await networkDelay();
    const url = new URL(request.url);
    const dateYmd = url.searchParams.get('date') ?? '';
    return HttpResponse.json<ServiceUserEnvelope<GetSlotsResponse>>({
      status: 'SUCCESS',
      message: 'Slots fetched',
      errorCode: 0,
      errorMessage: null,
      responseBody: {
        trainer_id: 'mock-teacher-id',
        trainer_name: 'Mock Trainer',
        trainer_type: 'inhouse',
        date: dateYmd,
        student_timezone: 'Asia/Kolkata',
        slots: buildMockSlots(dateYmd),
      },
    });
  }
);
